import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { ProductsContext } from '../App';
import HeroCarousel from './HeroCarousel';

const Home = React.memo(() => {
  const { products } = useContext(ProductsContext);
  const featured = products.slice(0, 3);
  
  const [imageIndices, setImageIndices] = useState({});

  const getFirstImage = (product) => {
    if (Array.isArray(product.images)) {
      return product.images[0];
    }
    if (typeof product.images === 'object' && product.images !== null) {
      return Object.values(product.images)[0];
    }
    return 'https://via.placeholder.com/300x300?text=No+Image';
  };

  const getDisplayPrice = (product) => {
    if (product.price !== undefined) {
      return product.price;
    }
    if (product.prices && typeof product.prices === 'object') {
      return Object.values(product.prices)[0];
    }
    return 0;
  };

  const getImageArray = (product) => {
    if (Array.isArray(product.images)) {
      return product.images;
    }
    if (typeof product.images === 'object' && product.images !== null) {
      return Object.values(product.images);
    }
    return [];
  };

  const handleImageHover = (productId) => {
    setImageIndices((prev) => {
      const product = featured.find((p) => p.id === productId);
      const imageArray = getImageArray(product);
      const currentIndex = prev[productId] || 0;
      const nextIndex = (currentIndex + 1) % imageArray.length;
      return { ...prev, [productId]: nextIndex };
    });
  };

  const handleMouseEnter = (productId) => {
    const product = featured.find((p) => p.id === productId);
    const imageArray = getImageArray(product);
    
    if (imageArray.length > 1) {
      const interval = setInterval(() => {
        handleImageHover(productId);
      }, 800);

      document.querySelector(`[data-product-id="${productId}"]`)?.setAttribute('data-interval', interval);
    }
  };

  const handleMouseLeave = (productId) => {
    const interval = document.querySelector(`[data-product-id="${productId}"]`)?.getAttribute('data-interval');
    if (interval) {
      clearInterval(parseInt(interval));
      document.querySelector(`[data-product-id="${productId}"]`)?.removeAttribute('data-interval');
    }
    setImageIndices((prev) => ({ ...prev, [productId]: 0 }));
  };

  return (
    <div>
      {/* Replace old hero section with carousel */}
      <HeroCarousel />

      {/* Featured Collections Section */}
      <div style={{ padding: '4rem 2rem', textAlign: 'center', maxWidth: '1200px', margin: '0 auto' }}>
        <h2 style={{ fontSize: '3rem', marginBottom: '3rem', letterSpacing: '1px' }}>
          Featured Collections
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '3rem' }} className="product-list">
          {featured.map((product) => {
            const imageArray = getImageArray(product);
            const currentImageIndex = imageIndices[product.id] || 0;
            const totalImages = imageArray.length || 1;
            const currentImage = imageArray[currentImageIndex % totalImages] || getFirstImage(product);

            return (
              <div 
                key={product.id} 
                className="product-card" 
                data-aos="zoom-in-up" 
                data-aos-delay="100"
                data-product-id={product.id}
                onMouseEnter={() => handleMouseEnter(product.id)}
                onMouseLeave={() => handleMouseLeave(product.id)}
              >
                <div className="product-image-container">
                  <div className="product-image-wrapper">
                    <img 
                      src={currentImage} 
                      alt={product.name} 
                      className="product-image"
                      style={{ height: '300px' }}
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/300x300?text=Image+Not+Found';
                      }}
                    />
                  </div>
                  {totalImages > 1 && (
                    <span className="image-indicator">
                      {currentImageIndex + 1}/{totalImages}
                    </span>
                  )}
                </div>
                <h4 style={{ fontSize: '1.8rem', margin: '1.5rem 0 0.5rem' }}>{product.name}</h4>
                <p style={{ fontSize: '1.2rem', color: '#777' }}>
                  ${getDisplayPrice(product).toFixed(2)}
                </p>
                <Link to={`/product/${product.id}`} style={{ fontSize: '1.1rem' }}>
                  Explore
                </Link>
              </div>
            );
          })}
        </div>
        <Link to="/shop" style={{ display: 'inline-block', marginTop: '6rem', fontSize: '1.2rem' }}>
          View All Collections
        </Link>
      </div>

      {/* Animated Banner Section */}
      <section className="banner-section">
        <div className="banner-decoration left">✨</div>
        <div className="banner-content">
          <h2>Elevate Your Elegance</h2>
          <p>
            Discover our exclusive collection of <span className="banner-highlight">handcrafted jewelry</span> 
            designed to enhance your natural beauty. Each piece tells a story of timeless craftsmanship and luxury.
          </p>
          <Link to="/shop" className="banner-button">
            Shop Premium Collection
          </Link>
        </div>
        <div className="banner-decoration right">💎</div>
      </section>

      {/* Our Legacy Section */}
      <section style={{ background: '#f9f9f9', padding: '4rem 2rem', textAlign: 'center' }}>
        <h2 style={{ fontSize: '3rem', marginBottom: '2rem' }}>Our Legacy</h2>
        <p style={{ maxWidth: '800px', margin: '0 auto', fontSize: '1.2rem', lineHeight: '1.8' }}>
          Crafting timeless jewellery with the finest materials, inspired by elegance and precision. 
          Discover pieces that tell your story and celebrate the moments that matter most.
        </p>
      </section>

      {/* Editorial Video Section */}
      <section className="editorial-video">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          style={{ width: '100%', height: '80vh', objectFit: 'cover', display: 'block', backgroundColor: '#000' }}
          onError={(e) => console.log('Video error:', e)}
        >
          <source src={process.env.PUBLIC_URL + '/video2.mp4'} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="video-overlay">
          <h2>
            <span>Crafted</span>
            {' '}
            <span>to</span>
            {' '}
            <span>Endure</span>
          </h2>
        </div>
      </section>
    </div>
  );
});

export default Home;